export enum UserRole {
  SYS_ADMIN = 'SYS_ADMIN',
  SYS_SUPPORT = 'SYS_SUPPORT',
  ORG_ADMIN = 'ORG_ADMIN',
  ORG_USER = 'ORG_USER',
}

export enum UserStatus {
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED',
}

export interface User {
  id: string;
  email: string;
  fullName: string;
  phoneNumber?: string;
  profilePicture?: string;
  profilePictureUrl?: string;
  password: string;
  role: UserRole;
  status: UserStatus;
  organizationId?: number;
  organizationName?: string;
  mustChangePassword?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface Organization {
  id: number;
  name: string;
  description?: string;
  contactEmail?: string;
  contactPhone?: string;
  website?: string;
  address?: string;
  city?: string;
  country?: string;
  mainBranchId?: number;
  branchCount: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateOrganizationRequest {
  name: string;
  description?: string;
  contactEmail: string;
  contactPhone: string;
  website?: string;
  address?: string;
  cityId: number;
  active?: boolean;
  mainBranch: {
    name: string;
    description?: string;
    businessCategoryIds: number[];
    address: string;
    cityId: number;
    latitude?: number;
    longitude?: number;
    commissionRate?: number | null;
    baseCommission?: number | null;
    minPeople?: number | null;
    maxPeople?: number | null;
  };
}

export interface UpdateOrganizationRequest {
  name?: string;
  description?: string;
  contactEmail?: string;
  contactPhone?: string;
  website?: string;
  address?: string;
  city?: number;
  active?: boolean;
}

export interface Branch {
  id: number;
  name: string;
  description?: string;
  address: string;
  city?: City;
  businessCategories: BusinessCategory[];
  images: string[];
  imageUrls?: string[];
  organizationId: number;
  organizationName: string;
  commissionRate: number;
  baseCommission: number;
  minPeople: number;
  maxPeople: number;
  active: boolean;
  isMainBranch: boolean;
  latitude?: number;
  longitude?: number;
  phone?: string;
  email?: string;
  website?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBranchRequest {
  name: string;
  description?: string;
  address: string;
  cityId: number;
  businessCategoryIds: number[];
  images?: string[];
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  commissionRate?: number | null;
  baseCommission?: number | null;
  minPeople?: number | null;
  maxPeople?: number | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface UpdateBranchRequest {
  name?: string;
  description?: string;
  address?: string;
  cityId?: number;
  businessCategoryIds?: number[];
  images?: string[];
  commissionRate?: number | null;
  baseCommission?: number | null;
  minPeople?: number | null;
  maxPeople?: number | null;
  active?: boolean;
  phone?: string | null;
  website?: string | null;
  email?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface UpdateUserRequest {
  email?: string;
  fullName?: string;
  phoneNumber?: string;
  role?: UserRole;
  organizationId?: number;
}

export interface BusinessCategory {
  id: number;
  name: string;
  code: string;
  emoji?: string;
  description?: string;
  active: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface BranchSummary {
  id: number;
  name: string;
  address: string;
  businessCategories: BusinessCategory[];
  organizationId: number;
}

export interface Campaign {
  id: number;
  branch: BranchSummary;
  title: string;
  description?: string;
  startDate: string;
  endDate: string;
  additionalCommissionRate: number;
  additionalFixedCommission: number;
  minPeopleRequired: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UserSummary {
  id: number;
  fullName: string;
  email: string;
  phoneNumber?: string;
  profilePicture?: string;
  profilePictureUrl?: string;
}

export interface JobListing {
  id: string;
  title: string;
  description: string;
  location?: string;
  city: City;
  totalBudget: number;
  currency: string;
  startDate: string;
  endDate?: string;
  paymentTermMonths?: number;
  paymentTermUnit?: string;
  paymentTermDescription?: string;
  status: string;
  createdBy: UserSummary;
  categories: BusinessCategory[];
  branch: BranchSummary;
  unit: UnitDto;
  quantity: number;
  benefits: string[];
  requirements: string[];
  createdAt: string;
  updatedAt: string;
  hasAcceptedApplicant?: boolean;
  acceptedApplicantName?: string;
  applicantCount?: number;
}

export interface JobApplication {
  id: string;
  jobListingId: string;
  applicantId: string;
  coverLetter?: string;
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED';
  createdAt: string;
  updatedAt: string;
}

export interface JobApplicationSummary {
  id: number;
  jobListingId: number;
  jobTitle: string;
}

export interface UnitDto {
  id: number;
  name: string;
  abbreviation: string;
  isActive: boolean;
}

export interface Reservation {
  id: number;
  branch: BranchSummary;
  businessOwner: UserSummary;
  businessPartner?: UserSummary;
  jobApplication?: JobApplicationSummary;
  status: string;
  unit: UnitDto;
  quantity: number;
  dateTime: string;
  endDateTime?: string;
  notes?: string;
  commission?: number;
  createdAt: string;
  updatedAt: string;
}

export interface DashboardStats {
  totalUsers: number;
  totalOrganizations: number;
  totalBranches: number;
  totalOperations: number;
  totalJobs: number;
  totalCampaigns: number;
  pendingUsers: number;
  totalRegistrationRequests: number;
  pendingRegistrationRequests: number;
  totalBranchRequests: number;
  pendingBranchRequests: number;
  recentOperations: Reservation[];
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface ApiError {
  message: string;
  status: number;
  code?: string;
}

export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

export interface SortOptions {
  field: string;
  direction: 'asc' | 'desc';
}

export interface FilterOptions {
  [key: string]: any;
}

export interface TableColumn {
  field: string;
  headerName: string;
  width?: number;
  sortable?: boolean;
  filterable?: boolean;
  renderCell?: (value: any, row: any) => React.ReactNode;
}

export interface Country {
  id: number;
  code: string;
  name: string;
  active: boolean;
}

export interface City {
  id: number;
  name: string;
  countryId: number;
  active: boolean;
}

export interface GeocodeRequest {
  address: string;
  city?: string;
  country?: string;
}

export interface GeocodeResponse {
  latitude: number;
  longitude: number;
  formattedAddress: string;
}

export interface OperationSummary {
  id: number;
  dateTime: string;
  status: string;
}

export interface OrganizationSummary {
  id: number;
  name: string;
}

export interface Review {
  id: number;
  operation: OperationSummary;
  branch: BranchSummary;
  reviewedOrganization: OrganizationSummary;
  reviewer: UserSummary;
  rating: number;
  comment?: string;
  referenceGiven: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BranchRequest {
  id: number;
  requestType: 'CREATE' | 'UPDATE';
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  originalBranch?: Branch;
  name: string;
  description?: string;
  address: string;
  city: City;
  businessCategories: BusinessCategory[];
  commissionRate?: number;
  baseCommission?: number;
  minPeople?: number;
  maxPeople?: number;
  documents: string[];
  images: string[];
  requestedBy: User;
  organization: Organization;
  reviewedBy?: User;
  reviewedAt?: string;
  reviewNotes?: string;
  createdAt: string;
  updatedAt: string;
}
